// Third party components
import PropTypes from 'prop-types';
import React from 'react';

import { Box, Typography } from '@mui/material';

// Shared components
import { LoadingSpinner, ContainerWithCard, LineLoader, Modal, PamLayoutGrid } from '@timmons-group/shared-react-components';

// Hooks and helpers
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '@timmons-group/shared-react-components/helpers';
import { useAuth } from '@timmons-group/shared-react-components/hooks';

// TODO: Get this to shared-react-components once DB is updated
import { useLayout } from 'hooks/useData';

import { useCommunity } from 'PAM/hooks/useCommunity';

import { ACLS } from 'constants';

// Constants
const ModalMode = {
  Edit: 'EDIT',
  Delete: 'DELETE',
};

const ModalAction = {
  Edit: 'EDIT',
  Delete: 'DELETE',
  Close: 'CLOSE',
  Reset: 'RESET',
};

// Reducers
const initialModalState = {
  modalIsOpen: false,
  modalTitle: '',
  modalText: '',
  okLabel: 'OK',
  modalMode: null,
  rowData: null,
};

const modalReducer = (state, action) => {
  const rowData = { ...action?.payload?.rowData };
  switch (action.type) {
    case ModalAction.Edit:
      return { modalIsOpen: true, modalText: `NOT IMPLEMENTED - Edit ${rowData?.name}`, modalMode: ModalMode.Edit, rowData };
    case ModalAction.Delete:
      return { modalTitle: 'Confirm Delete', modalIsOpen: true, modalText: `Are you sure you want to permanently delete this Plan from "${action?.payload?.community.name}"?`, modalMode: ModalMode.Delete, rowData, okLabel: 'Delete Plan' };
    case ModalAction.Close:
      return { ...state, modalIsOpen: false };
    case ModalAction.Reset:
      return { ...initialModalState };
    default:
      throw new Error('This mode is not supported.');
  }
}

const CommunityPlans = ({ id }) => {
  const { communityState, deletePlan } = useCommunity();
  const {loading, community, modifying} = communityState;
  const {plans} = community || {};
  const [layout, layoutLoading] = useLayout('plan', 'communityPlanList');
  const [modalState, modalDispatch] = React.useReducer(modalReducer, initialModalState);
  const { authState } = useAuth();

  // props.acl is a backdoor to allow storybook stories to pass in acls
  const acl = authState?.user?.acl;
  const nav = useNavigate();

  // Locals - TODO, do we want to pull actions from a DB layout?
  const handleEditClick = (row) => {
    nav(`/pam/communityArea/${id}/editPlan/${row.planID}`);
  }

  const handleDeleteClick = (row) => {
    modalDispatch({ type: ModalAction.Delete, payload: { rowData: row, community } });
  }

  let actionList = [];
  if (hasPermission(ACLS.EDIT_PLANNING_AREA, acl)) {
    actionList.push({
      label: 'Edit',
      order: 0,
      clickHandler: handleEditClick
    });
  }
  if (hasPermission(ACLS.DELETE_PLANNING_AREA, acl)) {
    actionList.push({
      label: 'Delete',
      order: 1,
      clickHandler: handleDeleteClick
    });
  }

  const actionData = [{
    order: 0,
    actionList,
  }];

  const handleModalAffirmative = () => {
    try {
      modalDispatch({ type: ModalAction.Close });

      switch (modalState.modalMode) {
        case ModalMode.Delete:
          requestDeletePlan(modalState.rowData?.planID);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Ensure that we clear the data
      modalDispatch({ type: ModalAction.Reset });
    }
  }

  const handleModalCancel = () => {
    modalDispatch({ type: ModalAction.Reset });
  }

  const requestDeletePlan = async (planID) => {
    if (!planID) {
      return;
    }

    const payload = {
      streamID: id,
      planID,
    };

    deletePlan(payload, modalState.rowData.name);
  }

  const renderPlans = () => {
    if (loading) {
      return <LineLoader message="Loading Plans..." color="secondary" />;
    }

    if (!plans?.length) {
      return <p>No plans found</p>;
    }

    return loading || layoutLoading ?
      (<ContainerWithCard>
        <LineLoader message="Loading All Plans..." color="secondary" />
      </ContainerWithCard>)
      :
      (<Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mt={2}
        minHeight="500px"
        width="100%"
      >
        <PamLayoutGrid
          actions={actionData}
          data={plans}
          getRowId={(row) => row.planID}
          initialSortColumn={'initiatedOn'}
          initialSortDirection={'desc'}
          layout={layout}
          autoHeight
          disableSelectionOnClick
          density="compact"
        />
      </Box>)
  };

  return (
    <>
      <LoadingSpinner isActive={modifying} />
      <ContainerWithCard maxWidth={false}>
        <h3>Plans</h3>
        <hr></hr>
        {renderPlans()}
      </ContainerWithCard>
      <Modal
        okLabel={modalState.okLabel || 'Confirm'}
        cancelLabel="Cancel"
        title={modalState.modalTitle}
        onOk={handleModalAffirmative}
        onCancel={handleModalCancel}
        handleClose={handleModalCancel}
        showX={true}
        open={modalState.modalIsOpen}
        hideActions={false}
        hideCancel={false}
      >
        <Typography gutterBottom>
          {modalState.modalText}
        </Typography>
      </Modal>
    </>
  );
};

CommunityPlans.propTypes = {
  id: PropTypes.string.isRequired,
};

export default CommunityPlans;
